/* eslint-disable jsx-a11y/no-noninteractive-element-interactions */
/* eslint-disable jsx-a11y/click-events-have-key-events */
import React, { useState, useCallback } from 'react';
import clsx from 'clsx';

interface AmenityProps {
  title: string;
  description: string;
  open?: boolean;
}

export const Amenity: React.FC<AmenityProps> = ({ title, description, open = false }) => {
  const [isOpen, setIsOpen] = useState(open);

  const toggleOpen = useCallback(() => setIsOpen((prev) => !prev), []);

  return (
    <details
      className={clsx(
        'bg-white hover:bg-emerald-50 rounded-lg border border-gray-200 hover:border-emerald-200 transition-all duration-200 overflow-hidden cursor-pointer',
      )}
      open={isOpen}
      onClick={(e) => {
        e.preventDefault();
        toggleOpen();
      }}
    >
      <summary
        className={clsx(
          'p-4 cursor-pointer select-none font-sans uppercase tracking-wider font-medium flex flex-row items-center justify-between text-sm',
          {
            'pb-2': isOpen,
          },
        )}
      >
        <span className="text-gray-900">{title}</span>
        <svg
          className={clsx('w-5 h-5 stroke-gray-700 transition-transform duration-200', {
            'rotate-180': isOpen,
          })}
          fill="none"
          stroke="currentColor"
          viewBox="0 0 24 24"
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth={2}
            d="M19 9l-7 7-7-7"
          />
        </svg>
      </summary>
      <div className="px-4 pb-4 text-sm border-t border-gray-100">
        <div className="pt-3 leading-relaxed text-gray-700">{description}</div>
      </div>
    </details>
  );
};

/**
 * Gallery image item - can be a string URL or an object with portrait flag
 */
export type GalleryImageItem = string | { imageUrl: string; portrait?: boolean };

interface GalleryProps {
  /** Array of image URLs or image objects with portrait flag */
  images: GalleryImageItem[];
  alt: string;
}

/** Helper to get imageUrl from a GalleryImageItem */
const getImageUrl = (item: GalleryImageItem): string =>
  typeof item === 'string' ? item : item.imageUrl;

/** Helper to check if an image is portrait */
const isPortrait = (item: GalleryImageItem): boolean =>
  typeof item === 'string' ? false : item.portrait === true;

export const Gallery: React.FC<GalleryProps> = ({ images, alt }) => {
  const [activeIndex, setActiveIndex] = useState(0);
  const activeImage = images[activeIndex];
  const activeIsPortrait = isPortrait(activeImage);

  return (
    <div className="space-y-4">
      {/* Main Image - fixed height to prevent jumping on mobile */}
      <div
        className={clsx(
          'relative w-full h-72 sm:h-96 md:h-[480px] overflow-hidden rounded-lg',
          activeIsPortrait ? 'bg-gray-100' : '',
        )}
      >
        <img
          src={getImageUrl(activeImage)}
          alt={`${alt} - ${activeIndex + 1} of ${images.length}`}
          className={clsx(
            'w-full h-full',
            activeIsPortrait ? 'object-contain' : 'object-cover',
          )}
        />
        {images.length > 1 && (
          <>
            <button
              onClick={() =>
                setActiveIndex((prev) => (prev === 0 ? images.length - 1 : prev - 1))
              }
              className="absolute left-4 top-1/2 -translate-y-1/2 bg-white/90 hover:bg-white p-3 rounded-full shadow-lg transition-colors"
            >
              <svg
                className="w-6 h-6"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M15 19l-7-7 7-7"
                />
              </svg>
            </button>
            <button
              onClick={() =>
                setActiveIndex((prev) => (prev === images.length - 1 ? 0 : prev + 1))
              }
              className="absolute right-4 top-1/2 -translate-y-1/2 bg-white/90 hover:bg-white p-3 rounded-full shadow-lg transition-colors"
            >
              <svg
                className="w-6 h-6"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M9 5l7 7-7 7"
                />
              </svg>
            </button>
          </>
        )}
      </div>

      {/* Thumbnails */}
      {images.length > 1 && (
        <div className="grid grid-cols-4 md:grid-cols-6 gap-2">
          {images.map((image, index) => (
            <button
              key={index}
              onClick={() => setActiveIndex(index)}
              className={clsx(
                'relative aspect-video overflow-hidden rounded-md border-2 transition-all',
                activeIndex === index
                  ? 'border-emerald-600 ring-2 ring-emerald-200'
                  : 'border-gray-200 hover:border-emerald-400',
              )}
            >
              <img
                src={getImageUrl(image)}
                alt={`Thumbnail ${index + 1}`}
                className="w-full h-full object-cover"
              />
            </button>
          ))}
        </div>
      )}
    </div>
  );
};
